import type { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Head from 'next/head';
import { useLayoutEffect } from 'react';

import { FAQBlock } from '../../components/landingV4/FAQBlock';
import { FeedbackBlock } from '../../components/landingV4/FeedbackBlock';
import { Footer } from '../../components/landingV4/Footer';
import { Header } from '../../components/landingV4/Header';
import { IntegrationsMatchCtaBlock } from '../../components/landingV4/IntegrationsMatchCtaBlock';
import { SnackbarProvider } from '../../components/landingV4/SnackbarProvider';
import { SolutionFeaturesBlock } from '../../components/landingV4/SolutionPage/SolutionFeaturesBlock';
import { SolutionPageHero } from '../../components/landingV4/SolutionPage/SolutionPageHero';
import { getSolutionPageBySlug, solutionPageSlugs } from '../../components/landingV4/SolutionPage/solutionPagesData';
import { EmptyLayout } from '../../components/layout/MainLayout/EmptyLayout';
import { NextPageWithLayout } from '../_app';

export const getStaticPaths: GetStaticPaths<{ slug: string }> = async ({ locales }) => {
  const effectiveLocales = locales && locales.length > 0 ? locales : ['en'];

  const paths = effectiveLocales.flatMap((locale) =>
    solutionPageSlugs.map((slug) => ({
      params: { slug },
      locale,
    }))
  );

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ locale, params }) => {
  const raw = params?.slug;
  const slug = typeof raw === 'string' ? raw : '';

  if (!getSolutionPageBySlug(slug)) {
    return { notFound: true };
  }

  return {
    props: {
      slug,
      ...(await serverSideTranslations(locale ?? 'en', ['common'])),
    },
  };
};

type PageProps = InferGetStaticPropsType<typeof getStaticProps>;

const Page: NextPageWithLayout = (props) => {
  const { slug } = props as PageProps;

  useLayoutEffect(() => {
    document.documentElement.classList.add('v4');
    document.documentElement.style.setProperty('--background', '#09090B');

    return () => {
      document.documentElement.classList.remove('v4');
      document.documentElement.style.removeProperty('--background');
    };
  }, []);

  const solution = getSolutionPageBySlug(slug);

  if (!solution) {
    return null;
  }

  const { headline, metaTitleSuffix, docDescription } = solution;
  const captionId = `solution-match-cta-${slug}`;

  return (
    <SnackbarProvider>
      <Head>
        <title>{`${metaTitleSuffix} | SmartGates`}</title>
        <meta name="description" content={docDescription} />
      </Head>

      <Header />

      <SolutionPageHero
        eyebrowLabel={solution.solutionEyebrowLabel}
        headline={headline}
        subheadline={solution.subheadline}
      />

      <SolutionFeaturesBlock sectionTitle={solution.featuresSectionTitle} items={solution.features} />

      <IntegrationsMatchCtaBlock
        headline={solution.ctaHeadline}
        asideCaptionId={captionId}
        visualCaption={solution.ctaVisualCaption}
      />

      <FAQBlock />
      <FeedbackBlock />
      <Footer />
    </SnackbarProvider>
  );
};

Page.getLayout = (page) => <EmptyLayout withFloatMenu={false}>{page}</EmptyLayout>;

export default Page;
